import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ApiError,
  UnauthorizedError,
  referenceLibraryApi,
} from '@/shared/api'
import type { CatalogDocumentDTO } from '@/shared/api'
import { useAuth } from '@/features/auth'
import { useReferenceSelection } from '@/features/references'
import { IngestionJobsPanel } from '@/features/documents'
import { Icon } from '@/shared/ui/Icon'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { ReferenceDocumentList } from './ReferenceDocumentList'
import { ReferenceUploadPanel } from './ReferenceUploadPanel'
import {
  filterReferenceDocuments,
  referenceTitle,
  sortReferenceDocumentsByRecency,
} from './referenceUtils'
import type {
  ReferenceListStatus,
  ReferenceUploadSuccessSummary,
} from './types'

function errorDetail(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.detail || fallback
  if (err instanceof Error) return err.message
  return fallback
}

/**
 * Reference Library workspace (Step 8.0).
 *
 * Mirrors ``DocumentsPage`` but only ever shows ``reference_document`` rows.
 * The page intentionally does NOT expose a "use as scope" CTA: multi-context
 * RAG (combining a case scope with a reference scope) is the next step. A
 * soft notice at the bottom of the page makes that explicit.
 */
export function ReferenceLibraryPage() {
  const { organization, logout } = useAuth()
  const tenantId = organization?.tenant_id ?? null
  const orgLabel = organization?.display_name ?? organization?.slug ?? '—'

  // The chat Reference Picker reads its document list from
  // ``ReferenceSelectionProvider`` (mounted above the shell), which only loads
  // its snapshot on tenant change. Without refreshing it here, a reference
  // uploaded or deleted in this session is missing from / stale in the picker
  // until a full page reload. This mirrors how ``DocumentsPage`` keeps
  // ``ScopeProvider`` in sync via ``reloadScope()`` after upload/delete.
  const { reload: reloadReferenceSelection } = useReferenceSelection()

  const [documents, setDocuments] = useState<CatalogDocumentDTO[]>([])
  const [status, setStatus] = useState<ReferenceListStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [recentUpload, setRecentUpload] =
    useState<ReferenceUploadSuccessSummary | null>(null)
  const [query, setQuery] = useState('')

  const [pendingDelete, setPendingDelete] =
    useState<CatalogDocumentDTO | null>(null)
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(
    null,
  )
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleteSuccess, setDeleteSuccess] = useState<{ title: string } | null>(
    null,
  )

  const loadDocuments = useCallback(async () => {
    if (!tenantId) return
    setStatus('loading')
    setError(null)
    try {
      const response = await referenceLibraryApi.list({ tenantId })
      setDocuments(sortReferenceDocumentsByRecency(response.items))
      setStatus('ready')
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        await logout()
        return
      }
      setError(errorDetail(err, 'Falha ao carregar a biblioteca de referência.'))
      setStatus('error')
    }
  }, [tenantId, logout])

  useEffect(() => {
    if (!tenantId) return
    void loadDocuments()
  }, [tenantId, loadDocuments])

  const handleUploaded = useCallback(
    async (summary: ReferenceUploadSuccessSummary) => {
      setRecentUpload(summary)
      await loadDocuments()
      // Keep the chat picker's snapshot current so the just-uploaded reference
      // is immediately selectable without a page reload.
      await reloadReferenceSelection()
    },
    [loadDocuments, reloadReferenceSelection],
  )

  // Phase 3B — a queued reference job finished in the background: refresh the
  // list + the chat picker snapshot so it shows up without a reload.
  const handleJobIndexed = useCallback(async () => {
    await loadDocuments()
    await reloadReferenceSelection()
  }, [loadDocuments, reloadReferenceSelection])

  const handleRequestDelete = useCallback((doc: CatalogDocumentDTO) => {
    setDeleteError(null)
    setDeleteSuccess(null)
    setPendingDelete(doc)
  }, [])

  const handleCancelDelete = useCallback(() => {
    if (deletingDocumentId) return
    setPendingDelete(null)
  }, [deletingDocumentId])

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete || !tenantId) return
    if (deletingDocumentId) return
    const target = pendingDelete
    setDeletingDocumentId(target.document_id)
    setDeleteError(null)
    try {
      await referenceLibraryApi.delete({
        tenantId,
        documentId: target.document_id,
      })
      setDocuments((prev) =>
        prev.filter((d) => d.document_id !== target.document_id),
      )
      setDeleteSuccess({ title: referenceTitle(target) })
      setPendingDelete(null)
      await loadDocuments()
      // Drop the removed reference from the chat picker's snapshot too — the
      // provider's reload also clears any now-dangling selection of it.
      await reloadReferenceSelection()
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        await logout()
        return
      }
      setDeleteError(errorDetail(err, 'Falha ao excluir a referência.'))
      setPendingDelete(null)
    } finally {
      setDeletingDocumentId(null)
    }
  }, [
    pendingDelete,
    tenantId,
    deletingDocumentId,
    loadDocuments,
    reloadReferenceSelection,
    logout,
  ])

  const filteredDocuments = useMemo(
    () => filterReferenceDocuments(documents, query),
    [documents, query],
  )

  if (!tenantId) {
    return (
      <div className="doc-workspace">
        <div className="doc-state doc-state-error">
          <div className="doc-state-icon">
            <Icon name="close" />
          </div>
          <h3>Sessão sem organização</h3>
          <p>Faça login novamente para acessar a biblioteca de referência.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="doc-workspace" data-testid="reference-library-page">
      <header className="doc-workspace-head">
        <div>
          <h1 className="doc-workspace-title">
            <Icon name="doc" /> Biblioteca de Referência
          </h1>
          <p className="doc-workspace-sub">
            Documentos reutilizáveis indexados para <strong>{orgLabel}</strong>.
            Use a Biblioteca de Referência para protocolos, leis, guias,
            políticas e bases internas.
          </p>
          <p className="doc-workspace-hint">
            Referências ainda não são usadas automaticamente nas consultas.
            A integração multi-contexto será adicionada em uma próxima etapa.
          </p>
        </div>
        <button
          type="button"
          className="doc-workspace-refresh"
          onClick={() => void loadDocuments()}
          disabled={status === 'loading'}
          title="Recarregar lista"
        >
          <Icon name="rotate" />
          {status === 'loading' ? 'Carregando…' : 'Atualizar'}
        </button>
      </header>

      <div className="doc-workspace-body">
        <section
          className="doc-workspace-list"
          aria-labelledby="ref-list-title"
        >
          <div className="doc-workspace-section-head">
            <h2 id="ref-list-title">
              Referências
              <span
                className="doc-list-count"
                title="Documentos exibidos / total"
              >
                {filteredDocuments.length === documents.length
                  ? documents.length
                  : `${filteredDocuments.length} / ${documents.length}`}
              </span>
            </h2>
            {deleteSuccess ? (
              <div
                className="doc-recent-pill is-success"
                title={deleteSuccess.title}
              >
                <Icon name="trash" />
                Excluído: {deleteSuccess.title}
              </div>
            ) : recentUpload && status === 'ready' ? (
              <div
                className="doc-recent-pill"
                title={recentUpload.document_id}
              >
                <Icon name="sparkles" />
                {recentUpload.status === 'queued'
                  ? 'Na fila'
                  : recentUpload.replaced
                    ? 'Reindexada'
                    : 'Nova'}
                : {recentUpload.document_id}
              </div>
            ) : null}
          </div>
          {deleteError ? (
            <div className="doc-state doc-state-error" role="alert">
              <div className="doc-state-icon">
                <Icon name="close" />
              </div>
              <p>{deleteError}</p>
            </div>
          ) : null}
          <ReferenceDocumentList
            documents={filteredDocuments}
            totalCount={documents.length}
            status={status}
            error={error}
            query={query}
            onQueryChange={setQuery}
            onRetry={() => void loadDocuments()}
            onRequestDelete={handleRequestDelete}
            deletingDocumentId={deletingDocumentId}
          />
        </section>

        <aside className="doc-workspace-side">
          <ReferenceUploadPanel
            tenantId={tenantId}
            organizationLabel={orgLabel}
            existingDocuments={documents}
            onUploaded={handleUploaded}
          />
          <IngestionJobsPanel
            tenantId={tenantId}
            refreshToken={recentUpload?.document_id ?? null}
            onIndexed={() => void handleJobIndexed()}
          />
        </aside>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Excluir referência?"
        message={
          pendingDelete
            ? buildDeleteMessage(pendingDelete)
            : ''
        }
        confirmLabel={
          deletingDocumentId ? 'Excluindo…' : 'Excluir referência'
        }
        cancelLabel="Cancelar"
        destructive
        busy={deletingDocumentId !== null}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={handleCancelDelete}
      />
    </div>
  )
}

function buildDeleteMessage(doc: CatalogDocumentDTO): string {
  const title = referenceTitle(doc)
  const chunkLabel =
    doc.chunk_count === 1 ? '1 chunk' : `${doc.chunk_count} chunks`
  return (
    `Isso removerá "${title}" (document_id: ${doc.document_id}) e os ` +
    `${chunkLabel} indexados. Essa ação não pode ser desfeita.`
  )
}
